// src/components/profile/ProfileBanner.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";

import styles from "../../styles/components/profile/ProfileBanner.module.css";
import ConfirmModal from "../common/ConfirmModal";
import { FaCamera, FaTrash } from "react-icons/fa";
import { MAX_IMAGE_UPLOAD_BYTES } from "../../constants/uploadLimits";

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      "image/jpeg",
      0.92
    );
  });
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });
}

export default function ProfileBanner({ user, onUpdated, isOwnProfile }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [minZoom, setMinZoom] = useState(1);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [localBanner, setLocalBanner] = useState("");
  const [cacheKey, setCacheKey] = useState(Date.now());

  const handlePick = () => fileRef.current?.click();

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    setLocalBanner(user?.banner || "");
  }, [user?.banner]);

  const uploadBanner = async (file) => {
    const form = new FormData();
    form.append("banner", file);
    try {
      setUploading(true);
      const res = await api.post("/users/me/banner", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLocalBanner(res.data.banner || "");
      setCacheKey(Date.now());
      onUpdated?.(res.data.banner);
      setCropOpen(false);
      setSelectedFile(null);
      setZoom(1);
      setMinZoom(1);
      setCroppedAreaPixels(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }
    } catch (err) {
      console.error("Banner upload failed", err);
      alert(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveBanner = () => {
    setShowRemoveModal(true);
  };

  const confirmRemoveBanner = async () => {
    try {
      setUploading(true);
      setShowRemoveModal(false);
      await api.delete("/users/me/banner");
      setLocalBanner("");
      setCacheKey(Date.now());
      onUpdated?.("");
    } catch (err) {
      console.error("Banner removal failed", err);
      alert(err.response?.data?.message || "Removal failed");
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      alert("Please select an image file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      alert("Image must be smaller than 5MB.");
      e.target.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setCroppedAreaPixels(null);
    setCropOpen(true);
    e.target.value = "";
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    setSelectedFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setCroppedAreaPixels(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    if (!previewUrl || !croppedAreaPixels || !selectedFile) return;

    try {
      const croppedBlob = await getCroppedImg(previewUrl, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], selectedFile.name, {
        type: "image/jpeg",
      });
      await uploadBanner(croppedFile);
    } catch (err) {
      console.error("Crop failed", err);
      alert("Failed to crop image. Please try again.");
    }
  };

  const getBannerSrc = () => {
    if (!localBanner) return null;
    const url = getImageUrl(localBanner);
    if (/[?&]t=/.test(url)) return url;
    return `${url}${url.includes("?") ? "&" : "?"}t=${cacheKey}`;
  };

  const src = getBannerSrc();

  return (
    <div className={styles.wrapper}>
      {src ? (
        <img src={src} alt="Profile Banner" className={styles.banner} />
      ) : (
        <div className={styles.bannerPlaceholder}>
          <div className={styles.placeholderContent}>
            {isOwnProfile && (
              <p className={styles.placeholderText}>
                Add a banner to personalize your profile
              </p>
            )}
          </div>
        </div>
      )}

      {isOwnProfile && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={handleChange}
            className={styles.hiddenInput}
          />
          <div className={styles.bannerActions}>
            <button
              type="button"
              className={styles.editBtn}
              onClick={handlePick}
              disabled={uploading}
              title="Change banner"
              aria-label="Change banner image"
            >
              <FaCamera className={styles.icon} />
            </button>
            {src && (
              <button
                type="button"
                className={styles.removeAction}
                onClick={handleRemoveBanner}
                disabled={uploading}
                title="Remove banner"
                aria-label="Remove banner image"
              >
                <FaTrash className={styles.icon} />
              </button>
            )}
          </div>
        </>
      )}

      {cropOpen && (
        <div className={styles.cropOverlay} onClick={handleCropCancel}>
          <div
            className={styles.cropModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.cropHeader}>
              <h3 className={styles.cropTitle}>Adjust your banner</h3>
              <p className={styles.cropSubtitle}>
                Drag to reposition. Use the slider to zoom.
              </p>
            </div>
            <div className={styles.cropArea}>
              <Cropper
                image={previewUrl}
                crop={crop}
                zoom={zoom}
                aspect={3}
                cropShape="rect"
                showGrid={false}
                objectFit="auto-cover"
                minZoom={minZoom}
                maxZoom={5}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                onMediaLoaded={(mediaSize) => {
                  // Calculate minimum zoom to fill the crop area
                  const cropAreaWidth = 900;
                  const cropAreaHeight = 300;
                  const minZoomX = cropAreaWidth / mediaSize.width;
                  const minZoomY = cropAreaHeight / mediaSize.height;
                  const calculatedMinZoom = Math.max(minZoomX, minZoomY);
                  setMinZoom(calculatedMinZoom);
                  setZoom(calculatedMinZoom);
                }}
              />
            </div>
            <div className={styles.zoomRow}>
              <span className={styles.zoomLabel}>Zoom</span>
              <input
                type="range"
                min={minZoom}
                max={5}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className={styles.zoomSlider}
              />
            </div>
            <div className={styles.cropActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={handleCropCancel}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleCropSave}
                disabled={uploading || !croppedAreaPixels}
              >
                {uploading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemoveModal && (
        <ConfirmModal
          message="Are you sure you want to remove your banner?"
          onConfirm={confirmRemoveBanner}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
    </div>
  );
}
